import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import Icon from '../Icons';
import './styles.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.eternavaults.com';

const SimplifiedDashboard = ({ currentMember, dashboardData, setShowAddMember, setShowFeatureExplorer }) => {
  const [userPersona, setUserPersona] = useState('simple'); // 'simple', 'power', 'admin'
  const [showAdvancedFeatures, setShowAdvancedFeatures] = useState(false);
  const [emergencyModalOpen, setEmergencyModalOpen] = useState(false);
  const [emergencyPlan, setEmergencyPlan] = useState(null);
  const { getAccessTokenSilently, user } = useAuth0();

  // Detect user persona based on usage patterns
  useEffect(() => {
    // Simple logic to determine user persona
    // In a real app, this would be based on user behavior analytics
    const memberCount = dashboardData?.familyMembers?.length || 0;
    const memoryCount = dashboardData?.memories?.length || 0;
    
    if (memberCount > 5 || memoryCount > 50) {
      setUserPersona('power');
    } else if (currentMember?.role === 'admin' || currentMember?.role === 'owner') {
      setUserPersona('admin');
    } else {
      setUserPersona('simple');
    }
  }, [dashboardData, currentMember]);

  // Define features based on user persona
  const getFeaturesForPersona = () => {
    const baseFeatures = [
      {
        title: 'Family Vault',
        description: 'Preserve stories, recipes, wishes & traditions. Schedule messages for birthdays & special dates.',
        icon: 'vault',
        link: `/family-vault/${currentMember._id}`,
        category: 'Legacy',
        priority: 'high'
      },
      {
        title: 'Upload Memory',
        description: 'Preserve photos, videos, letters, and more',
        icon: 'upload',
        link: `/upload-memory/${currentMember._id}`,
        category: 'Memories',
        priority: 'high'
      },
      {
        title: 'View Gallery',
        description: 'See all your uploaded memories',
        icon: 'gallery',
        link: `/gallery/${currentMember._id}`,
        category: 'Memories',
        priority: 'high'
      },
      {
        title: 'Family Tree',
        description: 'View and edit your family connections',
        icon: 'family-tree',
        link: '/family-tree',
        category: 'Family',
        priority: 'medium'
      },
      {
        title: 'Privacy Folders',
        description: 'Access your passcode-protected folders',
        icon: 'lock',
        link: `/privacy-folders/${currentMember._id}`,
        category: 'Security',
        priority: 'medium'
      }
    ];

    const advancedFeatures = [
      {
        title: 'Emergency Plan',
        description: 'Set up who can access your vault in case of emergency',
        icon: 'emergency',
        link: '#',
        category: 'Security',
        priority: 'low',
        isButton: true,
        buttonText: emergencyPlan ? 'Edit Emergency Plan' : 'Set Up Emergency Plan',
        onClick: () => setEmergencyModalOpen(true)
      },
      {
        title: 'Message Center',
        description: 'Communicate with family members',
        icon: 'message',
        link: '/message-center',
        category: 'Family',
        priority: 'low'
      },
      {
        title: 'Security Dashboard',
        description: 'View security settings and audit logs',
        icon: 'security',
        link: '/security',
        category: 'Security',
        priority: 'low'
      },
      {
        title: 'System Monitoring',
        description: 'View system health and performance',
        icon: 'monitoring',
        link: '/monitoring',
        category: 'Admin',
        priority: 'low'
      }
    ];

    // Return features based on persona
    switch (userPersona) {
      case 'simple':
        return {
          primary: baseFeatures.filter(f => f.priority === 'high'),
          secondary: baseFeatures.filter(f => f.priority === 'medium'),
          advanced: advancedFeatures
        };
      case 'power':
        return {
          primary: baseFeatures.filter(f => f.priority === 'high' || f.priority === 'medium'),
          secondary: baseFeatures.filter(f => f.priority === 'low'),
          advanced: advancedFeatures
        };
      case 'admin':
        return {
          primary: baseFeatures,
          secondary: advancedFeatures.filter(f => f.category !== 'Admin'),
          advanced: advancedFeatures.filter(f => f.category === 'Admin')
        };
      default:
        return {
          primary: baseFeatures.filter(f => f.priority === 'high'),
          secondary: baseFeatures.filter(f => f.priority === 'medium'),
          advanced: advancedFeatures
        };
    }
  };

  const features = getFeaturesForPersona();

  const getPersonaDescription = () => {
    switch (userPersona) {
      case 'simple':
        return 'Start with the basics - upload memories and view your gallery';
      case 'power':
        return 'You\'re using EternaVaults actively! Explore more features below';
      case 'admin':
        return 'Full access to all features and family management tools';
      default:
        return 'Welcome to your EternaVaults dashboard';
    }
  };

  const getPersonaIcon = () => {
    switch (userPersona) {
      case 'simple':
        return '👤';
      case 'power':
        return '⭐';
      case 'admin':
        return '👑';
      default:
        return '👤';
    }
  };

  return (
    <div className="simplified-dashboard">
      {/* Welcome Section */}
      <div className="dashboard-header">
        <div className="welcome-section">
          <div className="persona-indicator">
            <span className="persona-icon">{getPersonaIcon()}</span>
            <span className="persona-type">{userPersona.charAt(0).toUpperCase() + userPersona.slice(1)} User</span>
          </div>
          <h1>Welcome back, {currentMember?.name || 'Family Member'}!</h1>
          <p className="persona-description">{getPersonaDescription()}</p>
        </div>
        
        {/* Quick Stats */}
        <div className="quick-stats">
          <div className="stat-item">
            <Icon name="gallery" size={20} />
            <span className="stat-number">{dashboardData?.memories?.length || 0}</span>
            <span className="stat-label">Memories</span>
          </div>
          <div className="stat-item">
            <Icon name="family" size={20} />
            <span className="stat-number">{dashboardData?.familyMembers?.length || 0}</span>
            <span className="stat-label">Family Members</span>
          </div>
          <div className="stat-item">
            <Icon name="safe" size={20} />
            <span className="stat-number">{Math.round((dashboardData?.storageUsed || 0) / (1024 * 1024 * 1024) * 100) / 100}GB</span>
            <span className="stat-label">Storage Used</span>
          </div>
        </div>
      </div>

      {/* Primary Features - Always Visible */}
      <div className="features-section">
        <h2>Quick Actions</h2>
        <div className="features-grid primary">
          {features.primary.map((feature, index) => (
            <div key={index} className="feature-card primary">
              <div className="feature-icon">
                <Icon name={feature.icon} size={32} />
              </div>
              <div className="feature-content">
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
                {feature.isButton ? (
                  <button 
                    className="feature-button"
                    onClick={feature.onClick}
                  >
                    {feature.buttonText}
                  </button>
                ) : (
                  <Link to={feature.link} className="feature-link">
                    Open →
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Secondary Features - Collapsible */}
      {features.secondary.length > 0 && (
        <div className="features-section">
          <div className="section-header">
            <h2>More Features</h2>
            <button 
              className="toggle-button"
              onClick={() => setShowAdvancedFeatures(!showAdvancedFeatures)}
            >
              {showAdvancedFeatures ? 'Show Less' : 'Show More'}
              <Icon name={showAdvancedFeatures ? 'chevron-up' : 'chevron-down'} size={16} />
            </button>
          </div>
          
          {showAdvancedFeatures && (
            <div className="features-grid secondary">
              {features.secondary.map((feature, index) => (
                <div key={index} className="feature-card secondary">
                  <div className="feature-icon">
                    <Icon name={feature.icon} size={24} />
                  </div>
                  <div className="feature-content">
                    <h4>{feature.title}</h4>
                    <p>{feature.description}</p>
                    {feature.isButton ? (
                      <button 
                        className="feature-button small"
                        onClick={feature.onClick}
                      >
                        {feature.buttonText}
                      </button>
                    ) : (
                      <Link to={feature.link} className="feature-link small">
                        Open →
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Advanced Features - Admin Only */}
      {userPersona === 'admin' && features.advanced.length > 0 && (
        <div className="features-section admin">
          <h2>Admin Tools</h2>
          <div className="features-grid admin">
            {features.advanced.map((feature, index) => (
              <div key={index} className="feature-card admin">
                <div className="feature-icon">
                  <Icon name={feature.icon} size={24} />
                </div>
                <div className="feature-content">
                  <h4>{feature.title}</h4>
                  <p>{feature.description}</p>
                  {feature.isButton ? (
                    <button 
                      className="feature-button small"
                      onClick={feature.onClick}
                    >
                      {feature.buttonText}
                    </button>
                  ) : (
                    <Link to={feature.link} className="feature-link small">
                      Open →
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className="help-section">
        <div className="help-card">
          <Icon name="help" size={24} />
          <div className="help-content">
            <h3>Need Help?</h3>
            <p>Check out our guides or contact support</p>
            <div className="help-links">
              <Link to="/how" className="help-link">How It Works</Link>
              <Link to="/contact" className="help-link">Contact Support</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimplifiedDashboard;
